/**
 * SCM「入向解析库存比对」页签容器
 * 组合：人工粘贴解析面板 + 状态筛选 + 待审列表 + 详情抽屉
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card, Space, Input, Button, DatePicker, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ScmInboundParsePanel from './ScmInboundParsePanel';
import ScmInboundTable from './ScmInboundTable';
import ScmInboundDetailDrawer from './ScmInboundDetailDrawer';
import { scmService } from '../../../../api/enhancedApi';

const STATUS_TABS = [
    { key: 'active', label: '待处理', status: 'active' },
    { key: 'resolved', label: '已处理', status: 'resolved' },
];

const DEFAULT_PAGINATION = { current: 1, pageSize: 20, total: 0 };

const ScmInboundTab = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [activeTab, setActiveTab] = useState('active');
    const [stationFilter, setStationFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [detail, setDetail] = useState({ visible: false, pendingId: null });

    const loadPending = useCallback(async (override = {}) => {
        setLoading(true);
        try {
            const tab = STATUS_TABS.find((t) => t.key === (override.tabKey || activeTab)) || STATUS_TABS[0];
            const params = {
                page: pagination.current,
                page_size: pagination.pageSize,
                status: tab.status,
                station: stationFilter,
                ...override,
            };
            delete params.tabKey;
            if (!params.date_from && dateRange && dateRange[0] && dateRange[1]) {
                params.date_from = dateRange[0].format('YYYY-MM-DD');
                params.date_to = dateRange[1].format('YYYY-MM-DD');
            }
            Object.keys(params).forEach((k) => {
                if (params[k] === null || params[k] === undefined || params[k] === '') delete params[k];
            });
            const resp = await scmService.getScmPendingList(params);
            if (resp.data) {
                setDataSource(resp.data.items || []);
                setPagination((prev) => ({
                    ...prev,
                    total: resp.data.total || 0,
                    current: override.page || prev.current,
                }));
            }
        } catch (error) {
            console.error('加载 SCM 待审列表失败:', error);
            message.error('加载待审列表失败');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, activeTab, stationFilter, dateRange]);

    useEffect(() => {
        loadPending({ page: 1 });
    }, []);

    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
        loadPending({ page: newPagination.current, page_size: newPagination.pageSize });
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setPagination((prev) => ({ ...prev, current: 1 }));
        const tab = STATUS_TABS.find((t) => t.key === key) || STATUS_TABS[0];
        loadPending({ status: tab.status, page: 1, tabKey: key });
    };

    const handleStationSearch = (value) => {
        const v = value?.trim().toUpperCase() || undefined;
        setStationFilter(v);
        setPagination((prev) => ({ ...prev, current: 1 }));
        loadPending({ station: v, page: 1 });
    };

    const handleRefresh = () => loadPending({ page: pagination.current });

    const handleParsed = () => {
        setActiveTab('active');
        setPagination((prev) => ({ ...prev, current: 1 }));
        loadPending({ status: 'active', page: 1, tabKey: 'active' });
    };

    const handleView = (record) => {
        setDetail({ visible: true, pendingId: record.id });
    };

    const closeDetail = () => setDetail({ visible: false, pendingId: null });

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
        setPagination((prev) => ({ ...prev, current: 1 }));
        const override = { page: 1 };
        if (dates && dates[0] && dates[1]) {
            override.date_from = dates[0].format('YYYY-MM-DD');
            override.date_to = dates[1].format('YYYY-MM-DD');
        }
        loadPending(override);
    };

    const handleIgnore = async (record) => {
        try {
            const resp = await scmService.ignoreScmPending(record.id);
            message.success(resp.data?.message || '已忽略');
            handleRefresh();
        } catch (error) {
            message.error(error.response?.data?.detail || '忽略失败');
        }
    };

    return (
        <div>
            <ScmInboundParsePanel onParsed={handleParsed} />

            <Card
                size="small"
                title="SCM 比对待审记录"
                tabList={STATUS_TABS.map((t) => ({ key: t.key, tab: t.label }))}
                activeTabKey={activeTab}
                onTabChange={handleTabChange}
                extra={
                    <Space>
                        <Input
                            allowClear
                            placeholder="站点"
                            style={{ width: 90 }}
                            value={stationFilter || ''}
                            onChange={(e) => {
                                if (!e.target.value) handleStationSearch(undefined);
                                else setStationFilter(e.target.value.toUpperCase());
                            }}
                            onPressEnter={(e) => handleStationSearch(e.target.value)}
                        />
                        <DatePicker.RangePicker
                            size="small"
                            allowClear
                            style={{ width: 220 }}
                            value={dateRange}
                            onChange={handleDateRangeChange}
                        />
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>刷新</Button>
                    </Space>
                }
            >
                <ScmInboundTable
                    dataSource={dataSource}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    onView={handleView}
                    onIgnore={handleIgnore}
                />
            </Card>

            <ScmInboundDetailDrawer
                visible={detail.visible}
                pendingId={detail.pendingId}
                onClose={closeDetail}
                onChanged={handleRefresh}
            />
        </div>
    );
};

export default ScmInboundTab;
